import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X, Phone } from "lucide-react";

// Convert any YouTube URL (watch, youtu.be, embed, shorts) to an embed URL.
// Returns null if the URL is not a YouTube link.
const toYouTubeEmbed = (url) => {
  if (!url) return null;
  const m = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/,
  );
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
};

/*
  One shared, accessible demo modal used by every agent card.
  mode="video" -> autoplay video player (sound on, controls).
  mode="phone" -> AI Receptionist live-call popup with a tel: link.
  Closes on ESC / backdrop / X. Traps focus, returns focus to trigger,
  and respects prefers-reduced-motion.
*/
export const DemoModal = ({ open, onClose, agent, triggerRef }) => {
  const videoRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    const reduce = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    // autoplay video (sound on) when opened
    const v = videoRef.current;
    if (v) {
      v.currentTime = 0;
      v.play().catch(() => {});
    }

    // focus into the panel
    const panel = panelRef.current;
    const focusables = () =>
      panel
        ? Array.from(
            panel.querySelectorAll(
              'button, [href], video, input, [tabindex]:not([tabindex="-1"])',
            ),
          ).filter((el) => !el.disabled)
        : [];
    const first = focusables()[0];
    if (first) first.focus();

    const onKey = (e) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key === "Tab") {
        const els = focusables();
        if (els.length === 0) return;
        const firstEl = els[0];
        const lastEl = els[els.length - 1];
        if (e.shiftKey && document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      // pause + reset the video on close
      if (v) {
        v.pause();
        v.currentTime = 0;
      }
      // return focus to the element that opened the modal
      const t = triggerRef && triggerRef.current;
      if (t) t.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open || !agent) return null;

  const reduce =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const animCls = reduce ? "demo-modal-fade" : "demo-modal-in";

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={`${agent.name} demo`}
      data-testid="demo-modal"
      onClick={onClose}
    >
      <div
        className="absolute inset-0 bg-[#141210]/80 backdrop-blur-sm"
        data-testid="demo-modal-backdrop"
      />

      <div
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
        className={`relative z-10 w-full max-w-3xl overflow-hidden rounded-2xl bg-[var(--bg-base)] shadow-[0_40px_120px_-40px_rgba(20,18,16,0.7)] ${animCls}`}
      >
        <div className="flex items-center justify-between border-b border-black/5 px-6 py-4">
          <p className="text-sm font-semibold tracking-tight text-[var(--ink)]">
            {agent.name}
            <span className="ml-2 text-[var(--muted)]">
              {agent.cta === "live" ? "· Live demo" : "· Demo"}
            </span>
          </p>
          <button
            onClick={onClose}
            className="rounded-full p-2 text-[var(--ink)] transition-colors hover:bg-black/5"
            aria-label="Close demo"
            data-testid="demo-modal-close"
          >
            <X size={20} />
          </button>
        </div>

        {agent.cta === "live" ? (
          <div className="flex flex-col items-center px-6 py-14 text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-[var(--ember)]/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-[var(--ember)]">
              <Phone size={14} /> Live receptionist
            </span>
            <p className="mt-4 max-w-md text-base leading-relaxed text-[var(--muted)]">
              Call our AI receptionist live — hear it answer, qualify, and book,
              right now.
            </p>
            <a
              href={`tel:${(agent.phoneNumber || "").replace(/[^+\d]/g, "")}`}
              className="mt-6 block text-3xl font-semibold tracking-[-0.02em] text-[var(--ink)] sm:text-4xl"
              data-testid="demo-modal-phone"
            >
              {agent.phoneNumber}
            </a>
            <a
              href={`tel:${(agent.phoneNumber || "").replace(/[^+\d]/g, "")}`}
              className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-[var(--mint)] px-7 py-3.5 text-sm font-semibold text-[var(--charcoal)] transition-all duration-300 hover:bg-[var(--ember-deep)] hover:-translate-y-0.5"
              data-testid="demo-modal-call-now"
            >
              <Phone size={16} /> Call now
            </a>
          </div>
        ) : (
          (() => {
            const src = agent.demoVideoSrc || agent.demo_video || "";
            const yt = toYouTubeEmbed(src);
            return (
              <div className="relative aspect-video w-full bg-black">
                {yt ? (
                  <iframe
                    className="absolute inset-0 h-full w-full"
                    src={`${yt}?autoplay=1&rel=0&modestbranding=1`}
                    title={`${agent.name} demo`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    data-testid="demo-modal-video"
                  />
                ) : (
                  <video
                    ref={videoRef}
                    className="absolute inset-0 h-full w-full"
                    src={src}
                    controls
                    playsInline
                    data-testid="demo-modal-video"
                  />
                )}
              </div>
            );
          })()
        )}
      </div>
    </div>,
    document.body,
  );
};
